import { Box, Stack, SxProps, Theme, useTheme } from '@mui/material';
import { ComponentType, memo, PropsWithChildren, ReactNode } from 'react';
import { SystemStyleObject } from '@mui/system';

import { SCHEDULE } from '@schooly/constants/test-ids';

import { ItemLabel, ItemRoot, ItemTime } from '../TimeTabling/Items';
import { createScheduleColorPresets, useTimeTablingColors } from '../TimeTabling/utils';
import { useTruncatedText } from '../TruncatedText/useTruncatedText';
import { useScheduleGridContext } from './ScheduleGridContext';
import {
  ItemComponentProps,
  ScheduleGridItemData,
  ScheduleItemSize,
  TooltipComponentProps,
} from './types';
import { TWO_TONE_CLASSNAME, TwoToneBackground } from '../TwoToneBackground';

export type ScheduleGridItemProps<T> = {
  item: ScheduleGridItemData<T>;
  ItemComponent?: ComponentType<ItemComponentProps<T>>;
  TooltipComponent?: ComponentType<TooltipComponentProps<T>>;
};

const GAP = 2;

const ScheduleGridItemInner = <T,>({
  item,
  ItemComponent,
  TooltipComponent,
}: ScheduleGridItemProps<T>) => {
  const { getItemHeight, getItemTop, getItemSize } = useScheduleGridContext();

  const top = getItemTop(item.startTime);
  const fullHeight = getItemHeight(item.startTime, item.endTime);
  const contentHeight = fullHeight - GAP;
  const size = getItemSize(contentHeight);

  const content = ItemComponent ? (
    <ItemComponent item={item} size={size} height={contentHeight} />
  ) : (
    <ScheduleGridItemContent item={item} size={size} height={contentHeight} />
  );

  const wrappedContent: ReactNode = TooltipComponent ? (
    <TooltipComponent item={item} size={size}>
      {content}
    </TooltipComponent>
  ) : (
    content
  );

  return (
    <Box
      data-test-id={SCHEDULE.ITEM}
      sx={{
        position: 'absolute',
        top,
        height: contentHeight,
        left: 1,
        right: 1,
      }}
    >
      {item.dataTestId ? (
        <Box data-test-id={item.dataTestId} sx={{ height: '100%' }}>
          {wrappedContent}
        </Box>
      ) : (
        wrappedContent
      )}
    </Box>
  );
};

export const ScheduleGridItem = memo(ScheduleGridItemInner) as typeof ScheduleGridItemInner;

const MIN_HEIGHT_FOR_PADDING = 18;

export const ScheduleGridItemContent = <T,>({
  item,
  size,
  height,
  children,
  labelSx,
}: PropsWithChildren<ItemComponentProps<T> & { height: number; labelSx?: SxProps<Theme> }>) => {
  const theme = useTheme();
  const { resolveColorConfig } = useTimeTablingColors(createScheduleColorPresets(theme));
  const colorConfig = resolveColorConfig(item.color);
  const twoToneConfig = item.twoToneColor ? resolveColorConfig(item.twoToneColor) : undefined;
  const labels = Array.isArray(item.label)
    ? item.label.map((l) => String(l))
    : [String(item.label)];

  const { ref, containerRef, hiddenCount } = useTruncatedText({ itemsCount: labels.length });

  const rootSx = {
    position: 'relative',
    height: '100%',
    ...(twoToneConfig && {
      [TWO_TONE_CLASSNAME]: {
        path: { fill: twoToneConfig.bg },
      },
      '&:hover': {
        [TWO_TONE_CLASSNAME]: {
          path: { fill: twoToneConfig.bgHover },
        },
      },
    }),
  } as const;

  if (size === ScheduleItemSize.Minimal) {
    return (
      <ItemRoot colorConfig={colorConfig} sx={rootSx}>
        {!!twoToneConfig && <TwoToneBackground />}
      </ItemRoot>
    );
  }

  const withPadding = height >= MIN_HEIGHT_FOR_PADDING;

  const setRef = (el: HTMLElement | null) => {
    (ref as React.MutableRefObject<HTMLElement | null>).current = el;
    (containerRef as React.MutableRefObject<HTMLElement | null>).current = el;
  };

  return (
    <ItemRoot colorConfig={colorConfig} withPadding={withPadding} sx={rootSx}>
      {!!twoToneConfig && <TwoToneBackground />}
      <Stack
        position="relative"
        direction="row"
        gap={1}
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Stack sx={{ minWidth: 0 }} gap={0.5}>
          <ItemTime data-test-id={SCHEDULE.ITEM_TIME} variant="caption">
            {item.startTime} - {item.endTime}
          </ItemTime>
          {size === ScheduleItemSize.Full && (
            <Stack
              direction="row"
              gap={1}
              alignItems="center"
              sx={{ minWidth: 0, overflow: 'hidden' }}
            >
              <ItemLabel
                data-test-id={SCHEDULE.ITEM_GROUP_NAMES}
                onClick={item.onLabelClick}
                clickable={!!item.onLabelClick}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  ...((typeof labelSx === 'function'
                    ? labelSx(theme)
                    : labelSx) as SystemStyleObject),
                }}
              >
                <span
                  ref={setRef}
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {labels.map((name) => (
                    <span key={name}>{name}</span>
                  ))}
                </span>
                {hiddenCount > 0 && <span style={{ flexShrink: 0 }}>, +{hiddenCount}</span>}
              </ItemLabel>
            </Stack>
          )}

          {children}
        </Stack>
        {item.endAdornment?.({ size })}
      </Stack>
    </ItemRoot>
  );
};
